import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from '../useLatest'
import { useMaxDataDate } from '../../../hooks/useMaxDataDate'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import {
  fetchStaatidMerkeDetalj,
  fetchStaatidMerker,
  fetchStaatidRoundtrips,
  loadScatterSample,
  monthRange,
  formatDager,
  UNKNOWN_ERROR,
} from './api'
import type {
  StaatidParams,
  StaatidMerkeDetaljResponse,
  StaatidMerkerResponse,
  StaatidRoundtripsResponse,
} from './api'
import AlderFordeling from './AlderFordeling'
import MerkeScatter from './MerkeScatter'
import MonthlyTrendChart from './MonthlyTrendChart'
import TrailPanel from './TrailPanel'
import SubTabBar from '../SubTabBar'
import { useBruktbilAvansert } from '../tilgang'

// One section per tab (#677), the same layout ForhandlerDetalj got in #656.
// Local state, not the URL: the merke/modell is already deep-linked, and
// MerkerTab remounts this page per merke/modell so each opens on the first tab.
const DETALJ_TABS = [
  { key: 'enkeltsalg', labelKey: 'detalj.tabs.enkeltsalg' },
  { key: 'maanedstrend', labelKey: 'detalj.tabs.maanedstrend' },
  { key: 'bilalder', labelKey: 'detalj.tabs.bilalder' },
  { key: 'modeller', labelKey: 'detalj.tabs.modeller' },
] as const

type DetaljTab = (typeof DETALJ_TABS)[number]['key']

interface Props {
  merke: string
  /** Set when the page is scoped to a single model of that merke. */
  modell?: string | null
  /** Shared period/geo filters from the layout (no merke set). */
  baseFilters: StaatidParams
  onBack: () => void
  /** Opens the detail page for one of this merke's models. */
  onModellClick: (modell: string) => void
}

/**
 * One merke's (or model's) ståtid detail page (#490 AC1-AC4) — the Merke &
 * modell counterpart to ForhandlerDetalj: header KPIs above tabs for the
 * scatter with median and mean trend lines, the monthly trend, the age
 * breakdown, and (merke only) the model table.
 *
 * Every section loads together up front, so switching tabs sends no requests.
 */
export default function MerkeDetalj({ merke, modell, baseFilters, onBack, onModellClick }: Props) {
  const { t } = useTranslation('staatid')
  const [detalj, setDetalj] = useState<StaatidMerkeDetaljResponse | null>(null)
  const [modeller, setModeller] = useState<StaatidMerkerResponse | null>(null)
  const [scatter, setScatter] = useState<StaatidRoundtripsResponse | null>(null)
  const [zoomMonth, setZoomMonth] = useState<string | null>(null)
  const [trailSluttSvvId, setTrailSluttSvvId] = useState<number | null>(null)
  const [tab, setTab] = useState<DetaljTab>('enkeltsalg')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const maxDataDate = useMaxDataDate()
  const [tomt, setTomt] = useState(false)
  // The trail names every dealer in the chain — advanced only (#673).
  const avansert = useBruktbilAvansert()

  const filters: StaatidParams = {
    ...baseFilters,
    merke: [merke],
    ...(modell ? { modell: [modell] } : {}),
  }
  const filterKey = `${merke}|${modell ?? ''}|${baseFilters.from}|${baseFilters.to}|${baseFilters.selgerPostnummer ?? ''}`

  useLatest(alive => {
    setLoading(true)
    setError(null)
    setTomt(false)
    setZoomMonth(null)
    Promise.all([
      // 404 = this merke has no paired roundtrips in the period; that is an
      // empty state, not a failure, so it is caught rather than thrown.
      fetchStaatidMerkeDetalj(baseFilters, merke, modell).then(r => r.data).catch(() => null),
      fetchStaatidMerker(baseFilters, [merke], 500, 0),
      loadScatterSample(filters),
    ])
      .then(([d, m, r]) => {
        if (!alive()) return
        setDetalj(d)
        setTomt(d === null)
        setModeller(m.data)
        setScatter(r)
      })
      .catch(e => { if (alive()) setError(e instanceof Error ? e.message : UNKNOWN_ERROR) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [filterKey])

  function handleZoom(month: string | null) {
    setZoomMonth(month)
    const load = month
      ? fetchStaatidRoundtrips({ ...filters, ...monthRange(month) }, 2000, 0).then(r => r.data)
      : loadScatterSample(filters)
    load
      .then(setScatter)
      .catch(e => setError(e instanceof Error ? e.message : UNKNOWN_ERROR))
  }

  if (error) {
    return (
      <div className="card p-4 text-red-600 dark:text-red-400 text-sm">
        {t('common.loadError', { error: error === UNKNOWN_ERROR ? t('common.unknownError') : error })}
      </div>
    )
  }

  const tittel = modell ? `${merke} ${modell}` : merke

  return (
    <div className="space-y-4" data-testid="staatid-merke-detalj">
      <div className="card p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            data-testid="staatid-merke-detalj-back"
            className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> {t('common.back')}
          </button>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{tittel}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {modell ? t('detalj.modellSubtitle') : t('detalj.merkeSubtitle')}
            </p>
          </div>
        </div>
        {detalj && (
          <div className="flex flex-wrap gap-6 text-sm" data-testid="staatid-merke-kpi">
            <div><span className="text-gray-500 dark:text-gray-400">{t('common.stat.roundtrips')}</span> <strong>{detalj.antall.toLocaleString('nb-NO')}</strong></div>
            <div><span className="text-gray-500 dark:text-gray-400">{t('common.stat.median')}</span> <strong>{formatDager(detalj.median_dager)} d</strong></div>
            <div><span className="text-gray-500 dark:text-gray-400">{t('common.stat.snitt')}</span> <strong>{formatDager(detalj.snitt_dager)} d</strong></div>
          </div>
        )}
      </div>

      {tomt && !loading && (
        <div className="card p-4 text-sm text-gray-500 dark:text-gray-400" data-testid="staatid-merke-detalj-tomt">
          {t('detalj.tomt', { tittel })}
        </div>
      )}

      <SubTabBar
        parent={modell ? t('common.modell') : t('common.merke')}
        // A model has no models of its own, so it has no Modeller tab.
        tabs={(modell ? DETALJ_TABS.filter(d => d.key !== 'modeller') : DETALJ_TABS)
          .map(({ key, labelKey }) => ({ key, label: t(labelKey) }))}
        active={tab}
        onChange={setTab}
        testIdPrefix="staatid-merke-detalj-tab-"
      />

      {tab === 'enkeltsalg' && (
        <>
          <MerkeScatter
            rows={scatter?.rows ?? []}
            total={scatter?.total ?? 0}
            merker={[]}
            combinedTrend={detalj?.trend ?? []}
            combinedStats={detalj ? {
              antall: detalj.antall,
              median_dager: detalj.median_dager,
              snitt_dager: detalj.snitt_dager,
            } : undefined}
            loading={loading}
            zoomMonth={zoomMonth}
            onZoomMonth={handleZoom}
            onPointClick={avansert ? setTrailSluttSvvId : undefined}
            // The Månedlig trend tab shows exactly this series month by
            // month (#530), so the scatter's own table (#531) would be a copy.
            showTable={false}
          />

          {avansert && <TrailPanel sluttSvvId={trailSluttSvvId} onClose={() => setTrailSluttSvvId(null)} />}
        </>
      )}

      {/* #530 — the same monthly chart the dealer page got in #529. The detail
          response already carries median AND snitt per month, so this is a
          straight port: no new endpoint, no new shape. */}
      {tab === 'maanedstrend' && (
        <MonthlyTrendChart
          trend={detalj?.trend ?? []}
          from={baseFilters.from}
          to={baseFilters.to}
          loading={loading}
          scope={tittel}
          maxDataDate={maxDataDate}
        />
      )}

      {tab === 'bilalder' && (
        <AlderFordeling
          rows={detalj?.alder_fordeling ?? []}
          total={(detalj?.alder_fordeling ?? []).reduce((s, b) => s + b.antall, 0)}
          utenAlder={Math.max(0, (detalj?.antall ?? 0) - (detalj?.alder_fordeling ?? []).reduce((s, b) => s + b.antall, 0))}
          loading={loading}
          scope={tittel}
        />
      )}

      {tab === 'modeller' && !modell && (
        <div className="card overflow-hidden" data-testid="staatid-merke-detalj-modeller">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('detalj.modellerFor', { merke })}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
                <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.modell')}</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.antall')}</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.medianDager')}</th>
                <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.snittDager')}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {(modeller?.rows ?? []).map((row, i) => (
                <tr
                  key={`${row.modell}-${i}`}
                  data-testid="staatid-modell-rad"
                  onClick={() => row.modell && onModellClick(row.modell)}
                  className={`border-b border-gray-100 dark:border-dark-border cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                >
                  <td className="px-4 py-2">{row.modell ?? '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{row.antall.toLocaleString('nb-NO')}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.median_dager)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.snitt_dager)}</td>
                  <td className="px-4 py-2 text-right">
                    <ChevronRight className="w-4 h-4 inline text-primary-600 dark:text-primary-400" />
                  </td>
                </tr>
              ))}
              {!loading && (modeller?.rows ?? []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">{t('common.noData')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
